import { Clear, Check } from '@mui/icons-material';
import {
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Alert,
    CircularProgress,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';

import useTranslate from 'hooks/useTranslate';

interface SignatureModalProps {
    onClose: () => void;
    onSign: (signature: string) => void;
    isSubmitting?: boolean;
    /** Blocks another submit without the signing label, for example after a rate limit. */
    disabled?: boolean;
}

const SignatureBoard = ({
    onClose,
    onSign,
    isSubmitting = false,
    disabled = false,
}: SignatureModalProps) => {
    const { translate } = useTranslate();
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [hasSignature, setHasSignature] = useState(false);
    // Covers the click itself, before the parent flips `isSubmitting`
    const [applied, setApplied] = useState(false);
    const signing = isSubmitting || applied;
    const locked = signing || disabled;

    useEffect(() => {
        if (!isSubmitting) setApplied(false);
    }, [isSubmitting]);

    const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (locked) return;
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const rect = canvas.getBoundingClientRect();
        ctx.beginPath();
        ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
        setIsDrawing(true);
    };

    const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
        if (!isDrawing) return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const rect = canvas.getBoundingClientRect();
        ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.stroke();
        setHasSignature(true);
    };

    const stopDrawing = () => {
        setIsDrawing(false);
    };

    // Touch events for mobile
    const handleTouchStart = (e: React.TouchEvent<HTMLCanvasElement>) => {
        e.preventDefault();
        if (locked) return;
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const rect = canvas.getBoundingClientRect();
        const touch = e.touches[0];
        ctx.beginPath();
        ctx.moveTo(touch.clientX - rect.left, touch.clientY - rect.top);
        setIsDrawing(true);
    };

    const handleTouchMove = (e: React.TouchEvent<HTMLCanvasElement>) => {
        e.preventDefault();
        if (!isDrawing) return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const rect = canvas.getBoundingClientRect();
        const touch = e.touches[0];
        ctx.lineTo(touch.clientX - rect.left, touch.clientY - rect.top);
        ctx.strokeStyle = '#000';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.stroke();
        setHasSignature(true);
    };

    const handleTouchEnd = () => {
        setIsDrawing(false);
    };

    const clearSignature = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setHasSignature(false);
    };

    const applySignature = () => {
        const canvas = canvasRef.current;
        if (!canvas || !hasSignature || locked) return;

        // Convert canvas to base64 image
        const signatureData = canvas.toDataURL('image/png');
        setApplied(true);
        onSign(signatureData);
    };

    const handleClose = () => {
        if (signing) return;
        clearSignature();
        onClose();
    };

    return (
        <Box>
            <DialogContent>
                <Alert
                    severity="info"
                    icon={signing ? <CircularProgress size={18} color="inherit" /> : undefined}
                    sx={{ mb: 2 }}
                >
                    {signing
                        ? translate('signingAgreement')
                        : 'Please sign in the box below using your mouse or touch screen.'}
                </Alert>

                <Box
                    sx={{
                        border: 2,
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 2,
                        backgroundColor: '#fff',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                    }}
                >
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Sign here
                    </Typography>
                    <canvas
                        ref={canvasRef}
                        width={600}
                        height={300}
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                        onTouchStart={handleTouchStart}
                        onTouchMove={handleTouchMove}
                        onTouchEnd={handleTouchEnd}
                        style={{
                            border: '1px dashed #ccc',
                            cursor: 'default',
                            touchAction: 'none',
                            maxWidth: '100%',
                            pointerEvents: locked ? 'none' : 'auto',
                            opacity: locked ? 0.6 : 1,
                        }}
                    />
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button
                    onClick={clearSignature}
                    startIcon={<Clear />}
                    disabled={!hasSignature || locked}
                >
                    {translate('clearSignature')}
                </Button>
                <Box sx={{ flex: 1 }} />
                <Button onClick={handleClose} disabled={signing}>
                    {translate('cancel')}
                </Button>
                <Button
                    onClick={applySignature}
                    variant="contained"
                    startIcon={signing ? <CircularProgress size={16} color="inherit" /> : <Check />}
                    disabled={!hasSignature || locked}
                >
                    {signing ? translate('signingAgreement') : translate('applySignature')}
                </Button>
            </DialogActions>
        </Box>
    );
};

export default SignatureBoard;
